<script
  setup
  lang="ts"
  generic="
    ListingFilter extends {
      code: string;
      min?: number;
      max?: number;
      label: string;
    }
  "
>
import { reactive, ref, watch } from "vue";

const emits =
  defineEmits<
    (e: "select-value", value: { code: string; value: unknown }) => void
  >();

const props = defineProps<{
  filter: ListingFilter;
}>();

type Translations = {
  listing: {
    min: string;
    max: string;
  };
};
const translations: Translations = {
  listing: {
    min: "Min",
    max: "Max",
  },
};

const route = useRoute();
const minPrice = !Number.isNaN(Number(route.query["min-price"]))
  ? Number(route.query["min-price"])
  : props.filter?.min || 0;

const maxPrice = !Number.isNaN(Number(route.query["max-price"]))
  ? Number(route.query["max-price"])
  : props.filter?.max || 0;

const prices = reactive<{ min: number; max: number }>({
  min: minPrice,
  max: maxPrice,
});

const isFilterVisible = ref<boolean>(false);
const toggle = () => {
  isFilterVisible.value = !isFilterVisible.value;
};

const dropdownElement = ref(null);
onClickOutside(dropdownElement, () => {
  isFilterVisible.value = false;
});

function onMinPriceChange(newPrice: number, oldPrice: number) {
  if (newPrice === oldPrice || oldPrice === 0) return;
  emits("select-value", {
    code: "min-price",
    value: newPrice,
  });
}
const debounceMinPriceUpdate = useDebounceFn(onMinPriceChange, 500);
watch(() => prices.min, debounceMinPriceUpdate);

function onMaxPriceChange(newPrice: number, oldPrice: number) {
  if (newPrice === oldPrice || oldPrice === 0) return;
  emits("select-value", {
    code: "max-price",
    value: newPrice,
  });
}
const debounceMaxPriceUpdate = useDebounceFn(onMaxPriceChange, 500);
watch(() => prices.max, debounceMaxPriceUpdate);
</script>

<template>
  <div class="item border-t-0 border-b-2 border-b-cust_green">
    <button
      type="button"
      class="accordion-header w-full text-left py-4 px-3 font-bold flex justify-between items-center cursor-pointer bg-transparent"
      @click="toggle"
    >
      <h6 class="text-22 font-extrabold font-redit_sans_con tracking-[0.22px] capitalize capitalize">
        {{ $t('listing.price') }}
      </h6>

      <svg
        width="17"
        height="14"
        viewBox="0 0 16 9"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        class="transform transition-transform duration-200"
        :class="[!isFilterVisible ? 'rotate-0' : 'rotate-180']"
      >
        <path
          d="M15 1L7.99998 7L0.999983 1"
          stroke="#5C6566"
          stroke-width="2"
          stroke-linecap="round"
        />
      </svg>
    </button>

    <transition name="fade" mode="out-in">
      <div v-show="isFilterVisible" class="space-y-6 item border-t border-t-cust_gray px-3">
        <div class="my-6">
          <div class="w-full flex rounded-md mr-4">
            <span
              class="inline-flex items-center px-3 rounded-l-md border border-r-0 border-secondary-300 text-secondary-500 text-sm bg-cust_cream"
            >
              {{ $t('listing.min') }}
            </span>
            <input
              id="min-price"
              v-model="prices.min"
              type="number"
              name="min-price"
              class="inputField w-full"
              :placeholder="prices.min?.toString()"
            />
          </div>
          <div class="w-full flex rounded-md mt-3">
            <span
              class="inline-flex items-center px-3 rounded-l-md border border-r-0 border-secondary-300 text-secondary-500 text-sm bg-cust_cream"
            >
              {{ $t('listing.max') }}
            </span>
            <input
              id="max-price"
              v-model="prices.max"
              type="number"
              name="max-price"
              class="inputField w-full"
              :placeholder="prices.max?.toString()"
            />
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>
<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: all 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
.inputField {
  border-left: none;
  border-radius: 0 0.5rem 0.5rem 0;
}
</style>
